import React from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Label,
  Input,
  FormGroup,
  Button,
} from "reactstrap";
import { Plus, Trash2 } from "react-feather";
import { useTranslation } from "react-i18next";
import { titleCase } from "../../utils/case";
import { SOCIAL_PLATFORM_OPTIONS } from "../../utils/pageHelpers";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { getImageUrl } from "../../utils/image";
import { hasRole } from "../../utils/auth";

const General = ({
  content,
  updateField,
  updateNestedField,
  handleImageUpload,
}) => {
  const { t } = useTranslation();
  const canManage = hasRole(
    "superadmin",
    "finance"
  );
  const normalizePhones = () => {
    const phone = content.contactDetails?.phone;

    if (Array.isArray(phone)) return phone;
    if (phone) return [phone];

    return [""];
  };

  const normalizeSocialMedia = () => {
    const contactDetails = content.contactDetails || {};
    const socialMedia = contactDetails.socialMedia;

    if (Array.isArray(socialMedia)) return socialMedia;

    const legacyFields = [
      "facebook",
      "instagram",
      "x",
      "linkedin",
      "xhs",
      "tiktok",
    ];

    const legacyItems = legacyFields
      .filter((field) => contactDetails[field])
      .map((field) => ({
        platform: field,
        url: contactDetails[field],
      }));

    return legacyItems.length > 0
      ? legacyItems
      : [{ platform: "", url: "" }];
  };

  const updatePhone = (index, value) => {
    const phones = [...normalizePhones()];
    phones[index] = value;

    updateNestedField("contactDetails", "phone", phones);
  };

  const addPhone = () => {
    updateNestedField("contactDetails", "phone", [
      ...normalizePhones(),
      "",
    ]);
  };

  const removePhone = (index) => {
    const phones = normalizePhones().filter((_, i) => i !== index);

    updateNestedField(
      "contactDetails",
      "phone",
      phones.length > 0 ? phones : [""]
    );
  };

  const updateSocialMedia = (index, field, value) => {
    const socialMedia = [...normalizeSocialMedia()];

    socialMedia[index] = {
      ...socialMedia[index],
      [field]: value,
    };

    updateNestedField("contactDetails", "socialMedia", socialMedia);
  };

  const addSocialMedia = () => {
    updateNestedField("contactDetails", "socialMedia", [
      ...normalizeSocialMedia(),
      {
        platform: "",
        url: "",
      },
    ]);
  };

  const removeSocialMedia = (index) => {
    const socialMedia = normalizeSocialMedia().filter((_, i) => i !== index);

    updateNestedField(
      "contactDetails",
      "socialMedia",
      socialMedia.length > 0 ? socialMedia : [{ platform: "", url: "" }]
    );
  };

  const phones = normalizePhones();
  const socialMedia = normalizeSocialMedia();

  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <FormGroup className="mb-4">
            <Label>{titleCase(t("logo"))}</Label>

            {content.logo && (
              <div className="mb-3">
                <img
                  src={getImageUrl(content.logo)}
                  alt={titleCase(t("logo"))}
                  className="img-fluid rounded border"
                  style={{
                    maxHeight: "120px",
                    objectFit: "contain",
                  }}
                />
              </div>
            )}

            {canManage && (
              <Input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleImageUpload(
                    "logo",
                    e.target.files?.[0]
                  )
                }
              />
            )}
          </FormGroup>

          <FormGroup className="mb-4">
            <Label>{titleCase(t("website title"))}</Label>

            <Input
              value={content.websiteTitle || ""}
              onChange={(e) =>
                updateField("websiteTitle", e.target.value)
              }
              disabled={!canManage}
            />
          </FormGroup>

          <FormGroup>
            <Label>{titleCase(t("tagline"))}</Label>

            <Input
              value={content.tagline || ""}
              placeholder={titleCase(
                t("a short phrase describing your brand")
              )}
              onChange={(e) =>
                updateField("tagline", e.target.value)
              }
              disabled={!canManage}
            />
          </FormGroup>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardBody>
          <h6>{titleCase(t("contact details"))}</h6>

          <Row>
            <Col md="6">
              <div className="d-flex align-items-center gap-1 mb-2">
                <Label className="mb-0">
                  {titleCase(t("phone numbers"))}
                </Label>

                {canManage && (
                  <Plus
                    size={16}
                    role="button"
                    className="text-primary"
                    style={{ cursor: "pointer" }}
                    onClick={addPhone}
                    title={titleCase(t("add phone number"))}
                  />
                )}
              </div>

              {phones.map((phone, index) => (
                <Row key={index} className="mb-2">
                  <Col>
                    <Input
                      value={phone || ""}
                      placeholder={titleCase(t("phone number"))}
                      onChange={(e) => updatePhone(index, e.target.value)}
                      disabled={!canManage}
                    />
                  </Col>
                  {canManage && (
                    <Col xs="auto">
                      <Button
                        type="button"
                        size="sm"
                        color="danger"
                        outline
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "32px",
                          height: "32px",
                          padding: 0,
                        }}
                        disabled={phones.length === 1}
                        onClick={() => removePhone(index)}
                        title={titleCase(t("remove phone number"))}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </Col>
                  )}
                </Row>
              ))}
            </Col>

            <Col md="6">
              <FormGroup>
                <Label>{titleCase(t("email"))}</Label>
                <Input
                  value={content.contactDetails?.email || ""}
                  onChange={(e) =>
                    updateNestedField(
                      "contactDetails",
                      "email",
                      e.target.value
                    )
                  }
                  disabled={!canManage}
                />
              </FormGroup>

              <FormGroup>
                <Label>{titleCase(t("address"))}</Label>
                <Input
                  type="textarea"
                  rows="5"
                  value={content.contactDetails?.address || ""}
                  onChange={(e) =>
                    updateNestedField(
                      "contactDetails",
                      "address",
                      e.target.value
                    )
                  }
                  disabled={!canManage}
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">{titleCase(t("social media"))}</h6>
            {canManage && (
              <Button
                type="button"
                size="sm"
                color="primary"
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: "32px",
                  height: "32px",
                  padding: 0,
                }}
                onClick={addSocialMedia}
                title={titleCase(t("add social media"))}
              >
                <Plus size={16} />
              </Button>
            )}
          </div>

          {socialMedia.map((item, index) => (
            <Row key={index} className="mb-2 align-items-center">
              <Col md="4">
                <Typeahead
                  id={`social-platform-${index}`}
                  labelKey="label"
                  allowNew
                  disabled={!canManage}
                  options={SOCIAL_PLATFORM_OPTIONS.map((platform) => ({
                    label: platform,
                    value: platform,
                  }))}
                  selected={
                    item.platform
                      ? [
                          {
                            label: item.platform,
                            value: item.platform,
                          },
                        ]
                      : []
                  }
                  onChange={(selected) => {
                    const selectedItem = selected?.[0];

                    const value =
                      typeof selectedItem === "string"
                        ? selectedItem
                        : selectedItem?.value || selectedItem?.label || "";

                    updateSocialMedia(index, "platform", value);
                  }}
                  onInputChange={(value) =>
                    updateSocialMedia(index, "platform", value)
                  }
                  placeholder={titleCase(t("platform"))}
                />
              </Col>

              <Col>
                <Input
                  value={item.url || ""}
                  placeholder={titleCase(t("url"))}
                  onChange={(e) =>
                    updateSocialMedia(index, "url", e.target.value)
                  }
                  disabled={!canManage}
                />
              </Col>
              {canManage && (
                <Col md="1" className="d-flex justify-content-end">
                  <Button
                    type="button"
                    size="sm"
                    color="danger"
                    outline
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: "32px",
                      height: "32px",
                      padding: 0,
                    }}
                    disabled={socialMedia.length === 1}
                    onClick={() => removeSocialMedia(index)}
                    title={titleCase(t("remove social media"))}
                  >
                    <Trash2 size={16} />
                  </Button>
                </Col>
              )}
            </Row>
          ))}
        </CardBody>
      </Card>
    </>
  );
};

export default General;